// ============================================================================
// AM entry screen — the pure derivations. Run B Phase 2, 2026-08-18.
//
// Everything here is a pure function over the lines the AM has typed, so the
// rules are testable and there is exactly one place that states them. The screen
// renders what these return and never recomputes any of it inline.
//
// Nothing here invents a value. Where a rule cannot be applied honestly (a line
// with no rate, a job that is not finished) the answer is null, and the screen
// says so in words rather than showing a confident number built from a guess.
// ============================================================================

/** The margin floor the business quotes against. A hint, never a block. */
export const MARGIN_FLOOR_PCT = 30;

/**
 * How the sub picker is ordered. Jack's open question is whether capability
 * should beat "has worked here" or the reverse, so the rule is ONE named value
 * rather than an ordering smeared through a comparator. The tooltip the AM sees
 * is generated from it, so the screen can never claim an order it isn't using.
 *
 * Default is capability-first because that is what the mockup Jack approved
 * says in its own words: "Ranked by who can do this work, then who's worked
 * this facility." Flip this one constant if he rules the other way.
 */
// ⚠️ 2026-09-02 — `rankProviders` and the two constants below are NO LONGER
// RENDERED. The flat <select> they ordered was replaced by SmartSelect, which
// scopes to the subs who work this building and lets the AM type. The
// information the ranking carried has not left the screen: the "This service
// here" panel directly above the picker names who did this exact service at this
// facility and what they were paid.
//
// They are kept, with their tests, because Jack's question — should capability
// beat "has worked here", or the reverse — is still OPEN and unanswered, and
// deleting the constant would delete the question with it. ⛔ Do not wire them
// back into a second dropdown; if the ordering is wanted again it belongs inside
// SmartSelect, not beside it.
export type SubRankingRule = 'capability-first' | 'history-first';
export const SUB_RANKING_RULE: SubRankingRule = 'capability-first';

export const SUB_RANKING_TOOLTIP: Record<SubRankingRule, string> = {
  'capability-first':
    'Ranked by who can do this work, then by who has worked this facility. ' +
    'A sub who has done this exact service at this exact facility is always first.',
  'history-first':
    'Ranked by who has worked this facility, then by who can do this work. ' +
    'A sub who has done this exact service at this exact facility is always first.',
};

// ---------------------------------------------------------------------------
// The kind picker — step one is the money question. Run C Phase 1.
//
// Run B rendered "Project work — bill the client" and the five non-project
// divisions as six sibling pills, which deleted the only question on the screen
// that decides money: is the client being billed for this? These two helpers
// build the words for the binary FROM THE DATABASE, so the screen can name the
// alternatives and can never claim a division label that has since been renamed.
// ---------------------------------------------------------------------------

/** `["a", "b", "c"]` -> `"a, b or c"`. Empty list -> empty string. */
export function joinChoices(names: string[]): string {
  const clean = names.map((n) => n.trim()).filter(Boolean);
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0]!;
  return `${clean.slice(0, -1).join(', ')} or ${clean[clean.length - 1]!}`;
}

/**
 * The helper line under "Something else".
 *
 * The mockup's label — "Special pay — no client charge" — is FALSE. Verified in
 * `special_reasons` on 2026-08-19: the correction division holds `CLIENT_UNDERBILL`
 * and `MSA_RATE_CHANGE`, both of which bill the client, and `CLIENT_OVERBILL`,
 * which credits them. So the copy says most of these are not billed AND that a
 * correction goes whichever way the amount says.
 *
 * `correctionLabel` is read from v_event_classes, never typed here. When it is
 * missing (the division was retired, or the view failed to load) the sentence
 * degrades to the part that is still true rather than naming a division that
 * may not exist.
 */
export function secondOptionCaution(correctionLabel: string | null): string {
  const base = 'Usually nothing is billed to the client for these.';
  if (!correctionLabel) return base;
  return `${base} “${correctionLabel}” is the exception and goes either way — the amount you enter decides.`;
}

/**
 * What a line IS. Two kinds, and the difference is which way the money moves.
 *
 *   'service'     work performed. We pay a sub for it (sub_rate >= 0) and may
 *                 bill the client for it.
 *   'chargeback'  money recovered FROM a sub because of something they caused.
 *                 sub_rate is negative, there is no service_code (a chargeback
 *                 is not work), a reason is required, and the client is never
 *                 billed for it.
 *
 * Both live on the same project, so one card holds the whole event: pay Daniel
 * $100 to re-wax the floor, charge Alba $50 because her crew ruined it, bill the
 * client nothing.
 */
export type LineRole = 'service' | 'chargeback';

export interface EntryLine {
  /** Stable key for React only — never sent to the database. */
  key: string;
  /**
   * The persisted row this line is, or '' when it has not been saved yet.
   * Run C Phase 2 — the project detail page edits saved lines with this same
   * shape, so the editor is one component rather than two that drift.
   */
  service_line_id: string;
  line_role: LineRole;
  service_code: string;
  division_id: string;
  scope_text: string;
  scope_qty: string;
  assigned_partner_id: string;
  service_date_planned: string;
  service_date_actual: string;
  client_rate: string;
  sub_rate: string;
}

export function blankLine(key: string): EntryLine {
  return {
    key,
    service_line_id: '',
    line_role: 'service',
    service_code: '',
    division_id: '',
    scope_text: '',
    scope_qty: '',
    assigned_partner_id: '',
    service_date_planned: '',
    service_date_actual: '',
    client_rate: '',
    sub_rate: '',
  };
}

/** Blank string -> null; an explicit 0 stays 0. */
export function num(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function blankChargebackLine(key: string): EntryLine {
  return { ...blankLine(key), line_role: 'chargeback' };
}

/** True for a line that takes money back off a sub rather than paying it out. */
export function isChargeback(l: EntryLine): boolean {
  return l.line_role === 'chargeback';
}

/**
 * A line is complete enough to save when it says the thing its kind needs.
 *
 * A service line needs a service. A chargeback needs all four of the things the
 * DB constraint demands — who, how much, why, and no client charge — so the
 * screen refuses the same shapes the database would, in the same order, rather
 * than letting the AM discover a 23514 on save.
 */
export function lineIsSaveable(l: EntryLine, otherCode: string): boolean {
  if (isChargeback(l)) {
    if (!l.assigned_partner_id.trim()) return false;
    const amount = num(l.sub_rate);
    if (amount === null || amount === 0) return false;
    if (!l.scope_text.trim()) return false;
    return true;
  }
  if (!l.service_code.trim()) return false;
  // The escape hatch must carry words. "I don't see it" with nothing typed is
  // not a record of anything, and the DB check constraint refuses it anyway.
  if (l.service_code === otherCode && !l.scope_text.trim()) return false;
  return true;
}

/** What a chargeback line is still missing, in words, or null when it is ready. */
export function chargebackBlocker(l: EntryLine): string | null {
  if (!isChargeback(l)) return null;
  if (!l.assigned_partner_id.trim()) return 'choose which sub is being charged';
  const amount = num(l.sub_rate);
  if (amount === null || amount === 0) return 'enter how much to take off their pay';
  if (!l.scope_text.trim()) return 'say why — this one is not optional';
  return null;
}

/**
 * Project-level bill/pay from the lines.
 *
 * Summed ONLY when every line carries a rate. A partial sum would understate
 * the project and, worse, would make the line rates disagree with bill_amount —
 * which is exactly the condition the invoice builder uses to decide whether the
 * client sees an itemised invoice or one lump line (TCSD-0061). Returning null
 * leaves the amount unstated, which is honest and keeps the builder's guard
 * doing its job.
 */
export function deriveAmounts(lines: EntryLine[]): {
  bill_amount: number | null;
  pay_amount: number | null;
  everyLineHasClientRate: boolean;
  everyLineHasSubRate: boolean;
  /** Gross paid OUT to subs — what AP owes, before anything is recovered. */
  paid_out: number | null;
  /** Recovered FROM subs, as a positive number. 0 when there are no chargebacks. */
  charged_back: number;
  hasChargeback: boolean;
} {
  // A chargeback never bills the client, so it contributes nothing to the bill
  // and must not make bill_amount look "incomplete" for want of a client rate.
  const serviceLines = lines.filter((l) => !isChargeback(l));
  const chargebackLines = lines.filter(isChargeback);

  const clientRates = serviceLines.map((l) => num(l.client_rate));
  const everyLineHasClientRate = serviceLines.length > 0 && clientRates.every((r) => r !== null);

  const subRatesOut = serviceLines.map((l) => num(l.sub_rate));
  const everyLineHasSubRate = serviceLines.length > 0 && subRatesOut.every((r) => r !== null);

  const paid_out = everyLineHasSubRate
    ? Number(subRatesOut.reduce((a, b) => (a ?? 0) + (b ?? 0), 0)!.toFixed(2))
    : null;

  // Chargebacks are stored negative; report them as a positive "recovered".
  const charged_back = Number(
    chargebackLines
      .reduce((sum, l) => sum + Math.abs(num(l.sub_rate) ?? 0), 0)
      .toFixed(2),
  );

  // pay_amount is the NET cost of the event to TCS — paid out, less recovered.
  //
  // That is deliberately the net and not the gross, because pay_amount is what
  // trg_projects_compute_margin subtracts from bill_amount, and the margin on
  // this event genuinely is improved by money we get back. What AP owes each
  // individual sub is NOT this number — that lives per line, which is where
  // per-line payment already reads it from. One column cannot be both, so it is
  // the one the margin needs and the lines are the one AP needs.
  const pay_amount = paid_out === null ? null : Number((paid_out - charged_back).toFixed(2));

  return {
    bill_amount: everyLineHasClientRate
      ? Number(clientRates.reduce((a, b) => (a ?? 0) + (b ?? 0), 0)!.toFixed(2))
      : null,
    pay_amount,
    everyLineHasClientRate,
    everyLineHasSubRate,
    paid_out,
    charged_back,
    hasChargeback: chargebackLines.length > 0,
  };
}

/**
 * Project-level dates from the lines.
 *
 * start_date is the earliest thing anyone planned. completion_date is set ONLY
 * when every line has actually been done — a project with one line still
 * outstanding is not complete, and completion_date is a live input to the sub
 * payment mint and to both invoice builders, so guessing it moves money.
 *
 * The per-line planned date is never overwritten by any of this. Changing when
 * the work actually happened cannot erase what was first scheduled.
 */
export function deriveDates(lines: EntryLine[]): {
  start_date: string | null;
  completion_date: string | null;
  allLinesDone: boolean;
} {
  // Only work has dates. A chargeback records a decision about money, not a
  // visit, so counting it here would hold completion_date hostage to a date
  // there is no sensible value for.
  const work = lines.filter((l) => !isChargeback(l));
  const planned = work.map((l) => l.service_date_planned.trim()).filter(Boolean).sort();
  const actual = work.map((l) => l.service_date_actual.trim()).filter(Boolean).sort();
  const allLinesDone = work.length > 0 && actual.length === work.length;
  return {
    start_date: planned[0] ?? null,
    completion_date: allLinesDone ? actual[actual.length - 1]! : null,
    allLinesDone,
  };
}

/** Margin as a percentage of the client rate. Null unless both sides are known and the bill is positive. */
export function marginPct(clientRate: number | null, subRate: number | null): number | null {
  if (clientRate === null || subRate === null) return null;
  if (clientRate <= 0) return null;
  return ((clientRate - subRate) / clientRate) * 100;
}

export function clearsMarginFloor(pct: number | null): boolean | null {
  if (pct === null) return null;
  return pct >= MARGIN_FLOOR_PCT;
}

// ---------------------------------------------------------------------------
// Sub ranking.
// ---------------------------------------------------------------------------

export interface ProviderExperience {
  /** Times this provider has done THIS service at THIS facility. */
  jobsThisServiceHere: number;
  /** Times this provider has done THIS service anywhere. */
  jobsThisServiceAnywhere: number;
  /** Times this provider has worked THIS facility, on any service. */
  jobsAtThisFacility: number;
  /** True when the provider's history implies the capability this service needs. */
  hasCapability: boolean;
  /** Most recent date on any of the above, for tie-breaking. */
  lastServiceDate: string | null;
}

export interface RankableProvider {
  provider_id: string;
  provider_name: string | null;
  experience: ProviderExperience;
}

export interface RankedProvider extends RankableProvider {
  /** 0 is best. Rendered as a short reason, never as a number. */
  tier: number;
  reason: string;
}

/**
 * Rank providers for one service at one facility.
 *
 * Tier 0 is the same under either rule and is not up for debate: a sub who has
 * done this exact service at this exact facility. Tiers 1 and 2 are where
 * Jack's question lives, and swapping SUB_RANKING_RULE swaps exactly those two.
 */
export function rankProviders(
  providers: RankableProvider[],
  rule: SubRankingRule = SUB_RANKING_RULE,
): RankedProvider[] {
  const scored = providers.map((p): RankedProvider => {
    const e = p.experience;
    if (e.jobsThisServiceHere > 0) {
      return { ...p, tier: 0, reason: 'did this here' };
    }
    const capable = e.hasCapability || e.jobsThisServiceAnywhere > 0;
    const knowsSite = e.jobsAtThisFacility > 0;
    if (capable && knowsSite) return { ...p, tier: 1, reason: 'does this work · knows this site' };
    if (rule === 'capability-first') {
      if (capable) return { ...p, tier: 2, reason: 'does this work' };
      if (knowsSite) return { ...p, tier: 3, reason: 'has worked this site' };
    } else {
      if (knowsSite) return { ...p, tier: 2, reason: 'has worked this site' };
      if (capable) return { ...p, tier: 3, reason: 'does this work' };
    }
    return { ...p, tier: 4, reason: 'no history for this work' };
  });

  return scored.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    const d = (b.experience.lastServiceDate ?? '').localeCompare(a.experience.lastServiceDate ?? '');
    if (d !== 0) return d;
    return (a.provider_name ?? a.provider_id).localeCompare(b.provider_name ?? b.provider_id);
  });
}
